import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Avatar,
  TableSortLabel,
} from "@mui/material";
import { useState, useMemo } from "react";
import { formatDateToDDMMYY } from "../../../../../utils/helpers";
import DataStatusAlert from "../UI_HelpersOf_DataAccuracy/DataStatusAlert_Helpers";

interface StatusLogEntry {
  status: string;
  modified_at: string;
  modified_by: string;
  reason?: string;
}

interface StatusLogDialogProps {
  open: boolean;
  onClose: () => void;
  statusLog: StatusLogEntry[];
}

type SortField = "status" | "modified_at" | "modified_by" | "reason";
type SortOrder = "asc" | "desc";

const StatusLogDialog = ({
  open,
  onClose,
  statusLog,
}: StatusLogDialogProps) => {
  const [sortField, setSortField] = useState<SortField>("modified_at");
  const [sortOrder, setSortOrder] = useState<SortOrder>("asc");

  const handleSort = (field: SortField) => {
    if (sortField === field) {
      setSortOrder(sortOrder === "asc" ? "desc" : "asc");
    } else {
      setSortField(field);
      setSortOrder("asc");
    }
  };

  const sortedStatusLog = useMemo(() => {
    if (!statusLog || statusLog.length === 0) return [];

    return [...statusLog].sort((a, b) => {
      let comparison = 0;

      switch (sortField) {
        case "status":
          comparison = (a.status || "").localeCompare(b.status || "");
          break;
        case "modified_at":
          comparison =
            new Date(a.modified_at).getTime() -
            new Date(b.modified_at).getTime();
          break;
        case "modified_by":
          comparison = (a.modified_by || "").localeCompare(b.modified_by || "");
          break;
        case "reason":
          comparison = (a.reason || "").localeCompare(b.reason || "");
          break;
      }

      return sortOrder === "asc" ? comparison : -comparison;
    });
  }, [statusLog, sortField, sortOrder]);

  return (
    <Dialog
      open={open}
      onClose={(_, reason) => {
        if (reason !== "backdropClick") {
          onClose();
        }
      }}
      maxWidth="md"
      fullWidth
    >
      <DialogTitle>
        <Typography variant="h6">Status History Log</Typography>
      </DialogTitle>
      <DialogContent>
        <TableContainer>
          <Table stickyHeader size="small">
            <TableHead>
              <TableRow>
                <TableCell>
                  <TableSortLabel
                    active={sortField === "status"}
                    direction={sortField === "status" ? sortOrder : "asc"}
                    onClick={() => handleSort("status")}
                  >
                    Status
                  </TableSortLabel>
                </TableCell>
                <TableCell>
                  <TableSortLabel
                    active={sortField === "modified_at"}
                    direction={sortField === "modified_at" ? sortOrder : "asc"}
                    onClick={() => handleSort("modified_at")}
                  >
                    Modified On
                  </TableSortLabel>
                </TableCell>
                <TableCell>
                  <TableSortLabel
                    active={sortField === "modified_by"}
                    direction={sortField === "modified_by" ? sortOrder : "asc"}
                    onClick={() => handleSort("modified_by")}
                  >
                    Modified By
                  </TableSortLabel>
                </TableCell>
                <TableCell>
                  <TableSortLabel
                    active={sortField === "reason"}
                    direction={sortField === "reason" ? sortOrder : "asc"}
                    onClick={() => handleSort("reason")}
                  >
                    Reason
                  </TableSortLabel>
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {sortedStatusLog.map((entry, index) => {
                const userName =
                  entry.modified_by?.split("@")[0] || entry.modified_by;

                return (
                  <TableRow key={index}>
                    <TableCell>
                      <DataStatusAlert status={entry.status} />
                    </TableCell>
                    <TableCell>
                      {entry.modified_at
                        ? formatDateToDDMMYY(entry.modified_at)
                        : "-"}
                    </TableCell>
                    <TableCell>
                      <Box
                        sx={{ display: "flex", alignItems: "center", gap: 1 }}
                      >
                        <Avatar
                          sx={{
                            width: 24,
                            height: 24,
                            fontSize: "0.75rem",
                            bgcolor: "#9c27b0",
                          }}
                        >
                          {userName?.charAt(0)?.toUpperCase() || "U"}
                        </Avatar>
                        <Typography variant="body2">
                          {entry.modified_by}
                        </Typography>
                      </Box>
                    </TableCell>
                    <TableCell>{entry.reason || " "}</TableCell>
                  </TableRow>
                );
              })}
              {sortedStatusLog.length === 0 && (
                <TableRow>
                  <TableCell colSpan={4} align="center">
                    <Typography variant="body2" color="text.secondary">
                      No status history available
                    </Typography>
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </DialogContent>
      <DialogActions
        sx={{
          px: 3,
          py: 2,
          justifyContent: "flex-end",
        }}
      >
        <Button onClick={onClose} variant="contained" color="secondary">
          Close
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default StatusLogDialog;
